import * as React from "react"
import styled from "styled-components"
import {debounce} from "lodash"

import id from "@irati/utils/id"
import Icon from "@irati/ui/src/Icon"
import {getPlaceSuggestions} from "@irati/geolocation/src/helpers/places"
import {greys} from "@irati/ui/src/global/colors"
import {injectIntl} from "@irati/utils/lang"
import {LatLng} from "@irati/geolocation/src/types"

import {getCurrentPosition} from "@irati/geolocation/src/helpers/devicePosition"
import CustomRenderer from "./CustomRenderer"
import StyledSelect from "./styled/Select"
import {GeoInputSelection} from "../../types"

const InverseGeoPin = styled(Icon)`
  stroke: ${greys.black};
`

interface GeoInputValue {
  latLng?: LatLng | null,
  label: string,
  icon: React.ReactNode
}

interface GeoInputOption {
  label: string,
  icon: React.ReactNode,
  value: GeoInputValue
}

interface Props {
  onChange: (data: GeoInputSelection) => void,
  value: GeoInputValue,
  formatMessage: (params: Object) => string,
  useCurrentPositionFallback: boolean,
  userLatLng: LatLng
}

interface State {
  value: GeoInputValue | Object,
  isFetchingLocation: boolean,
  options: GeoInputOption[]
}

export const SELECTED_VALUE_ICON = <InverseGeoPin color={greys.white} icon="geoPin" size={14} />

class GeoInput extends React.Component<Props, State> {
  currentLocationOption: GeoInputOption

  static defaultProps = {
    label: null,
    placeholder: "geolocation.components.geoInput.defaultProps.placeholder",
    autoload: false,
    name: "geoInput",
    valueKey: "value",
    labelKey: "label",
    value: null,
    loadingPlaceholder: "geolocation.components.geoInput.defaultProps.loadingPlaceholder",
    optionRenderer: (selection: GeoInputOption) => <CustomRenderer selection={selection} />,
    valueRenderer: (selection: GeoInputValue) => <CustomRenderer selection={selection} />,
    arrowRenderer: null,
    useCurrentPositionFallback: true
  }

  constructor(props: Props) {
    super(props)
    this.loadOptions = debounce(this.loadOptions, 500)
    const {formatMessage} = props
    const label = formatMessage({id: "geolocation.components.geoInput.label.useCurrentLocation"})
    const icon = <Icon color={greys.black} icon="geoArrow" size={12} />
    this.currentLocationOption = {
      label,
      icon,
      value: {
        latLng: null,
        label,
        icon
      }
    }
    this.state = {
      value: props.value,
      isFetchingLocation: false,
      options: [this.currentLocationOption]
    }
  }

  componentDidUpdate = () => {
    if (this.props.value && !this.state.value) {
      this.setState({
        value: this.props.value
      })
    }
  }

  handleChange = (selection: GeoInputValue): void => {
    const {onChange, formatMessage, useCurrentPositionFallback} = this.props
    if (!selection) {
      return this.setState({
        value: {}
      })
    }

    if (selection.latLng) {
      this.setState({value: selection})
      return onChange({
        latLng: selection.latLng,
        isAccurate: true,
        desc: selection.label
      })
    }

    this.showFetchingLocationIndicator()
    getCurrentPosition({useFallback: useCurrentPositionFallback})
      .then((pos) => {
        this.hideFetchingLocationIndicator()
        const myCurrentLocationLabel = formatMessage({id: "geolocation.components.geoInput.label.myCurrentLocation"})
        const currentPosItem = {
          latLng: pos.latLng,
          label: myCurrentLocationLabel,
          icon: SELECTED_VALUE_ICON
        }
        this.setState({value: currentPosItem})
        onChange({
          latLng: currentPosItem.latLng,
          isAccurate: !pos.isFallback,
          desc: myCurrentLocationLabel
        })
      })
      .catch(() => {
        this.hideFetchingLocationIndicator()
        this.setState({
          value: {
            label: formatMessage({id: "geolocation.components.geoInput.label.getBrowserCurrentPositionError"}),
            icon: <Icon color={greys.black} icon="info" size={16} />
          }
        })
      })
  }

  showFetchingLocationIndicator = () => {
    this.setState({isFetchingLocation: true})
  }

  hideFetchingLocationIndicator = () => {
    this.setState({isFetchingLocation: false})
  }

  loadOptions = (query: string) => {
    if (query) {
      this.showFetchingLocationIndicator()
      const {userLatLng} = this.props
      getPlaceSuggestions(query, userLatLng).then((data) => {
        this.hideFetchingLocationIndicator()
        const suggestions = data.map((suggestion) => {
          const label = suggestion.desc
          const icon = SELECTED_VALUE_ICON
          return {
            label,
            icon,
            value: {
              latLng: {
                lat: suggestion.lat,
                lng: suggestion.lng
              },
              label,
              icon
            }
          }
        })
        this.setState({
          options: [this.currentLocationOption].concat(suggestions)
        })
      })
    }
    this.setState({
      options: [this.currentLocationOption]
    })
  }

  selectCurrentLocation = () => {
    this.handleChange(this.currentLocationOption.value)
  }

  render() {
    const {isFetchingLocation, options, value} = this.state
    return (
      <StyledSelect
        {...this.props}
        isLoading={isFetchingLocation}
        options={options}
        onChange={this.handleChange}
        onInputChange={this.loadOptions}
        id={id("geo-input", this.props)}
        value={value}
        filterOptions={false} />
    )
  }
}

export default injectIntl(GeoInput, {withRef: true})
